/**
 * React Query hooks for strategy configuration data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getStrategyConfigs,
  getStrategyConfig,
  createStrategyConfig,
  deleteStrategyConfig,
  getStrategyConfigRuns,
  getStrategyConfigVersions,
  getConfigPaperSessions,
  getAggregationPaperSessions,
  getAggregationOptimizations,
  getStrategyConfigBestRuns,
  getAggregationRuns,
  getAggregationVersions,
  getAggregations,
} from '../api/client.js';

export function useStrategyConfigs(filters?: { strategy?: string; symbol?: string; timeframe?: string; userStrategyId?: string }) {
  return useQuery({
    queryKey: ['strategy-configs', filters],
    queryFn: () => getStrategyConfigs(filters),
    refetchInterval: 30000,
  });
}

export function useStrategyConfig(id: string | null) {
  return useQuery({
    queryKey: ['strategy-config', id],
    queryFn: () => getStrategyConfig(id!),
    enabled: !!id,
  });
}

export function useStrategyConfigRuns(id: string | null) {
  return useQuery({
    queryKey: ['strategy-config-runs', id],
    queryFn: () => getStrategyConfigRuns(id!),
    enabled: !!id,
  });
}

export function useStrategyConfigVersions(strategy: string, symbol: string, timeframe: string) {
  return useQuery({
    queryKey: ['strategy-config-versions', strategy, symbol, timeframe],
    queryFn: () => getStrategyConfigVersions(strategy, symbol, timeframe),
    enabled: !!strategy && !!symbol && !!timeframe,
  });
}

export function useConfigPaperSessions(configId: string | null) {
  return useQuery({
    queryKey: ['config-paper-sessions', configId],
    queryFn: () => getConfigPaperSessions(configId!),
    enabled: !!configId,
    refetchInterval: 15000,
  });
}

export function useAggregationPaperSessions(aggregationId: string | null) {
  return useQuery({
    queryKey: ['aggregation-paper-sessions', aggregationId],
    queryFn: () => getAggregationPaperSessions(aggregationId!),
    enabled: !!aggregationId,
    refetchInterval: 15000,
  });
}

/**
 * Fetch all GS and WF optimization runs owned by this portfolio (migration 065).
 * Returns StrategyOptimizationSummary[] ordered by most-recent first.
 */
export function useAggregationOptimizations(aggregationId: string | null) {
  return useQuery({
    queryKey: ['aggregation-optimizations', aggregationId],
    queryFn: () => getAggregationOptimizations(aggregationId!),
    enabled: !!aggregationId,
    staleTime: 60_000,
  });
}

export function useAggregationRuns(aggregationId: string | null) {
  return useQuery({
    queryKey: ['aggregation-runs', aggregationId],
    queryFn: () => getAggregationRuns(aggregationId!),
    enabled: !!aggregationId,
  });
}

export function useStrategyConfigBestRuns(configIds: string[] | undefined) {
  return useQuery({
    queryKey: ['strategy-config-best-runs', ...(configIds ?? [])],
    queryFn: () => getStrategyConfigBestRuns(configIds!),
    enabled: !!configIds && configIds.length > 0,
  });
}

/**
 * Fetch version history for a portfolio (aggregation config).
 * Used by the Portfolios page version picker.
 */
export function useAggregationVersions(aggregationId: string | null) {
  return useQuery({
    queryKey: ['aggregation-versions', aggregationId],
    queryFn: () => getAggregationVersions(aggregationId!),
    enabled: !!aggregationId,
    staleTime: 60_000,
  });
}

/**
 * Fetch all aggregation configs (portfolios), filtered to exclude aof1- records.
 */
export function usePortfolios() {
  return useQuery({
    queryKey: ['portfolios'],
    queryFn: async () => {
      const all = await getAggregations();
      return all.filter((a) => !a.id.startsWith('aof1-'));
    },
    staleTime: 30_000,
  });
}

export function useCreateStrategyConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createStrategyConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategy-configs'] });
    },
  });
}

export function useDeleteStrategyConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteStrategyConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategy-configs'] });
    },
  });
}
